import { useEffect, useState } from "react";

import { MessageDisplay } from "./components/Chat/MessageDisplay";
import { MessageHeader } from "./components/Chat/MessageHeader";
import { highlightRequestVariables } from "./components/InputVariableHighlighting/highlightRequestVariables";
import { preStyles } from "./utils/preStyles";
import { getMediaContent, getStringContent } from "./utils/utils";

import { ChatTemplate, Message } from "@/types";
import InlineWarning from "@/components/InlineWarning";

export const Chat = ({
  inputVariables,
  startOpen = false,
  highlight = false,
  isOpen,
  onToggle,
  message,
  chatTemplate,
  isGoogle,
}: {
  inputVariables?: Record<string, string>;
  startOpen?: boolean;
  highlight?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  message?: Message;
  chatTemplate: ChatTemplate;
  isGoogle?: boolean;
}) => {
  const [componentIsOpen, setComponentIsOpen] = useState<boolean>(startOpen);

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setComponentIsOpen(!componentIsOpen);
    }
  };
  useEffect(() => {
    if (isOpen !== undefined && !!isOpen !== componentIsOpen) {
      setComponentIsOpen(!!isOpen);
    }
  }, [isOpen, setComponentIsOpen, componentIsOpen]);

  const role = message?.raw_request_display_role || message?.role || "";

  const content = message ? getStringContent(message) : "";
  const mediaContent = message ? getMediaContent(message) : [];
  const hasImageContent =
    message?.content?.some(
      c =>
        (c.type === "image_url" && "image_url" in c) ||
        (c.type === "output_media" &&
          "url" in c &&
          (c as { media_type?: string }).media_type === "image"),
    ) ?? false;
  const highlightedContent = inputVariables
    ? highlightRequestVariables(content, inputVariables)
    : content;
  const isJsonSchema = Boolean(chatTemplate.json_schema?.json_schema);
  const hasTools = !!chatTemplate.tools?.length;
  const hasFunctions = !!chatTemplate.functions?.length;
  // Check if the message itself has tool_calls (for assistant responses with tool calls but no text content)
  const messageHasToolCalls = !!(message && "tool_calls" in message && message.tool_calls?.length);
  const showEmptyMessageWarning =
    !content &&
    mediaContent.length === 0 &&
    !hasImageContent &&
    !isJsonSchema &&
    !hasTools &&
    !hasFunctions &&
    !messageHasToolCalls &&
    ["model", "assistant"].includes(role);

  if (!message && !isJsonSchema && !hasTools && !hasFunctions) {
    return null;
  }

  return (
    <>
      <InlineWarning show={showEmptyMessageWarning} />
      <MessageHeader
        componentIsOpen={componentIsOpen}
        handleToggle={handleToggle}
        message={message}
        chatTemplate={chatTemplate}
      >
        {componentIsOpen ? (
          <pre className={`whitespace-pre-wrap rounded-b-sm ${preStyles(role)} p-2 text-black`}>
            <MessageDisplay
              isJsonSchema={isJsonSchema}
              componentIsOpen
              highlightedContent={highlightedContent}
              highlight={highlight}
              message={message}
              chatTemplate={chatTemplate}
              isGoogle={isGoogle}
            />
          </pre>
        ) : (
          <MessageDisplay
            isJsonSchema={isJsonSchema}
            componentIsOpen={false}
            highlightedContent={highlightedContent}
            highlight={highlight}
            message={message}
            chatTemplate={chatTemplate}
            isGoogle={isGoogle}
          />
        )}
      </MessageHeader>
    </>
  );
};
